export type CsuiteRole = 'CSO' | 'CFO' | 'CTO' | 'COO' | 'CPO' | 'CEO' | 'CMO';

export interface PersonaConfig {
  id: CsuiteRole;
  name: string;
  title: string;
  description: string;
  color: string;
  gradient: string;
  icon: string;
  focus: string[];
  aiContext: string;
  quickActions: string[];
  documentTypes: string[];
}

export const PERSONA_CONFIGS: Record<CsuiteRole, PersonaConfig> = {
  CSO: {
    id: 'CSO',
    name: 'Chief Strategy Officer',
    title: 'Strategic Leadership',
    description: 'Focus on long-term strategy, market analysis, and competitive positioning',
    color: 'from-purple-500 to-blue-600',
    gradient: 'bg-gradient-to-br from-purple-500 to-blue-600',
    icon: '🎯',
    focus: ['Strategic Planning', 'Market Analysis', 'Competitive Intelligence', 'Business Development'],
    aiContext: 'You are a Chief Strategy Officer focused on strategic planning, market analysis, competitive positioning, and long-term business growth. Provide strategic insights and recommendations.',
    quickActions: ['Strategic Plan', 'Market Research', 'Competitive Analysis'],
    documentTypes: ['strategic_plan', 'business_plan', 'market_research', 'swot_analysis', 'business_model_canvas', 'sop', 'org_chart', 'board_strategy']
  },
  CFO: {
    id: 'CFO',
    name: 'Chief Financial Officer',
    title: 'Financial Leadership',
    description: 'Focus on financial planning, budgeting, forecasting, and risk management',
    color: 'from-green-500 to-emerald-600',
    gradient: 'bg-gradient-to-br from-green-500 to-emerald-600',
    icon: '💰',
    focus: ['Financial Planning', 'Budget Management', 'Risk Assessment', 'Investment Analysis'],
    aiContext: 'You are a Chief Financial Officer focused on financial strategy, budgeting, forecasting, risk management, and investment decisions. Provide financial insights and recommendations with specific numbers and financial metrics.',
    quickActions: ['Financial Forecast', 'Budget Analysis', 'Cash Flow Model', 'Financial Reports'],
    documentTypes: ['financial_forecast', 'budget_template', 'cash_flow_statement', 'income_statement', 'balance_sheet', 'financial_dashboard', 'business_plan', 'kpi_creation']
  },
  CTO: {
    id: 'CTO',
    name: 'Chief Technology Officer',
    title: 'Technology Leadership',
    description: 'Focus on technology strategy, innovation, and digital transformation',
    color: 'from-blue-500 to-cyan-600',
    gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    icon: '⚡',
    focus: ['Technology Strategy', 'Innovation', 'Digital Transformation', 'Cybersecurity'],
    aiContext: 'You are a Chief Technology Officer focused on technology strategy, innovation, digital transformation, cybersecurity, and technical architecture. Provide technology insights with specific technical recommendations and implementation roadmaps.',
    quickActions: ['Tech Roadmap', 'Security Policy', 'Digital Strategy', 'Architecture Plan'],
    documentTypes: ['innovation_roadmap', 'tech_architecture', 'cybersecurity_policy', 'digital_transformation_plan', 'strategic_plan', 'sop']
  },
  COO: {
    id: 'COO',
    name: 'Chief Operating Officer',
    title: 'Operational Excellence',
    description: 'Focus on operations, processes, efficiency, and execution',
    color: 'from-orange-500 to-red-600',
    gradient: 'bg-gradient-to-br from-orange-500 to-red-600',
    icon: '⚙️',
    focus: ['Operations Management', 'Process Optimization', 'Quality Control', 'Supply Chain'],
    aiContext: 'You are a Chief Operating Officer focused on operations management, process optimization, efficiency improvements, quality control, and execution excellence. Provide operational insights with specific process improvements and efficiency metrics.',
    quickActions: ['Process Map', 'Efficiency Analysis', 'Quality Standards', 'Operations Plan'],
    documentTypes: ['sop', 'process_optimization', 'quality_management', 'operations_manual', 'kpi_creation', 'okr']
  },
  CPO: {
    id: 'CPO',
    name: 'Chief Product Officer',
    title: 'Product Leadership',
    description: 'Focus on product strategy, user experience, and market fit',
    color: 'from-pink-500 to-rose-600',
    gradient: 'bg-gradient-to-br from-pink-500 to-rose-600',
    icon: '🚀',
    focus: ['Product Strategy', 'User Experience', 'Product-Market Fit', 'Customer Research'],
    aiContext: 'You are a Chief Product Officer focused on product strategy, user experience, product-market fit, customer research, and product development. Provide product insights with specific user-focused recommendations and data-driven decisions.',
    quickActions: ['Product Roadmap', 'User Research', 'Product Strategy', 'Feature Analysis'],
    documentTypes: ['product_roadmap', 'user_research_plan', 'product_requirements', 'go_to_market_strategy', 'customer_acquisition_plan', 'market_research', 'innovation_roadmap']
  },
  CEO: {
    id: 'CEO',
    name: 'Chief Executive Officer',
    title: 'Executive Leadership',
    description: 'Focus on vision, leadership, decision-making, and overall business performance',
    color: 'from-slate-700 to-slate-900',
    gradient: 'bg-gradient-to-br from-slate-700 to-slate-900',
    icon: '👑',
    focus: ['Executive Leadership', 'Strategic Vision', 'Stakeholder Management', 'Performance Oversight'],
    aiContext: 'You are a Chief Executive Officer focused on executive leadership, strategic vision, stakeholder management, and overall business performance. Provide high-level strategic guidance and leadership insights.',
    quickActions: ['Strategic Vision', 'Performance Review', 'Stakeholder Report', 'Executive Summary'],
    documentTypes: ['strategic_plan', 'business_plan', 'board_strategy', 'org_chart', 'kpi_creation', 'okr', 'swot_analysis', 'financial_forecast', 'budget_template', 'market_research', 'pitch_deck', 'business_model_canvas', 'go_to_market_strategy']
  },
  CMO: {
    id: 'CMO',
    name: 'Chief Marketing Officer',
    title: 'Marketing Leadership',
    description: 'Focus on brand strategy, customer acquisition, market positioning, and growth marketing',
    color: 'from-indigo-500 to-purple-600',
    gradient: 'bg-gradient-to-br from-indigo-500 to-purple-600',
    icon: '📈',
    focus: ['Brand Strategy', 'Customer Acquisition', 'Market Positioning', 'Growth Marketing'],
    aiContext: 'You are a Chief Marketing Officer focused on brand strategy, customer acquisition, market positioning, and growth marketing. Provide marketing insights with specific strategies for brand building and customer growth.',
    quickActions: ['Marketing Strategy', 'Brand Analysis', 'Customer Journey', 'Growth Plan'],
    documentTypes: ['strategic_plan', 'business_plan', 'market_research', 'go_to_market_strategy', 'customer_acquisition_plan', 'swot_analysis', 'pitch_deck', 'business_model_canvas', 'user_research_plan', 'kpi_creation']
  }
};
